import { produce } from "immer";
import React, { useContext, useEffect } from "react";
import styled from "styled-components";

import { Button, Toolbar } from "@examspark/common-ui";
import { scoreFractionModel } from "@examspark/score";
import { Stem, StemFractionModel } from "@examspark/types";

import Mark from "../../common/Mark";
import QuestionContext from "../../QuestionContext";
import PieChart from "../common/graph/pie-chart/PieChart";
import Prompt from "../shared/Prompt";

import ItemFractionModelCell from "./FractionModelCell";

export const MAX_NUMBER_OF_SHADEABLES = 10000;
export const MAX_NUMBER_OF_SLICES = 18;

const Row = styled.div`
  display: flex;
  border-top: solid 1px #333;

  &:first-child {
    border-top: none;
  }
`;

const StyledFigure = styled.figure`
  width: fit-content;
`;

const StyledFigcaption = styled.figcaption`
  font-size: 2em;
  font-weight: 500;

  max-width: 50rem;
  text-align: center;
`;

interface MarkProps {
  mark?: "correct" | "incorrect";
}

const Styled = styled.div<MarkProps>`
  display: flex;
  flex-wrap: wrap;
  position: relative;
  padding: 2rem 1.5rem;
  border-top: solid 3px transparent;
  max-width: 50rem;

  ${(props) => {
    if (props.mark === "correct") {
      return `
      background-color: #e4f3e4;
      border-top: solid 3px #9cb39c;
      `;
    }
    if (props.mark === "incorrect") {
      return `
      background-color: #f3e2e2;
      border-top: solid 3px #b32e2b;
      `;
    }
  }}

  .markup {
    position: absolute;
    top: -11px;
    right: -8px;
  }
`;

const Matrix = styled.div`
  /* padding: 0.1rem; */
  border: solid 1.25px #333;
  background-color: #fff;
  margin: 1rem 1rem;
`;

const initArray = (length: number) => Array.from(Array(length).keys());

export interface ItemFractionModelProps {
  data: StemFractionModel;
}

const FractionModel: React.FC<
  React.PropsWithChildren<ItemFractionModelProps>
> = (props) => {
  const [player, dispatchPlayer] = useContext(QuestionContext);

  // config values are stored as string. So make sure they're usable
  // (number, non-negative) and parse
  const slices = Math.max(Number(props.data.config.model?.slices), 1) || 1;
  const shaded = Number(props.data.scoring?.shaded?.[0].value) || 0;
  const groups = Math.max(Number(props.data.config.model?.groups), 1) || 1;
  const columns = Math.max(Number(props.data.config.model?.columns), 1) || 1;
  const rows = Math.max(Number(props.data.config.model?.rows), 1) || 1;

  const buildMatrix = () => {
    if (props.data.config.model?.shape === "pie") {
      return initArray(1).map(() =>
        initArray(groups).map(() => initArray(slices).map(() => 0)),
      );
    }
    if (props.data.config.model?.shape === "bar") {
      return initArray(groups).map(() =>
        initArray(rows).map(() => initArray(columns).map(() => 0)),
      );
    }
    return [];
  };

  const response:
    | {
        shaded?: Stem.Response["shaded"];
        shadedWithGroups?: Stem.Response["shadedWithGroups"];
      }
    | undefined = player.response?.[props.data.id] ?? {
    shadedWithGroups: buildMatrix(),
  };

  // Reveal Key
  useEffect(() => {
    if (player.revealKey) {
      const answer = buildMatrix();

      let cellsLeftToShade = shaded;
      answer.forEach((group, groupIdx) => {
        group.forEach((row, rowIdx) => {
          row.forEach((col, colIdx) => {
            answer[groupIdx][rowIdx][colIdx] = cellsLeftToShade-- > 0 ? 1 : 0;
          });
        });
      });

      dispatchPlayer({
        type: "RESPONSE",
        data: {
          score: true,
          id: props.data.id,
          response: {
            shadedWithGroups: answer,
          },
        },
      });
    }
  }, [player.revealKey]);

  const handleCellClick = (group: number, row: number, col: number) => {
    if (!response) return;
    dispatchPlayer({
      type: "RESPONSE",
      data: {
        id: props.data.id,
        response: produce(response, (draft) => {
          if (!draft.shadedWithGroups) {
            draft.shadedWithGroups = buildMatrix();
          }

          draft.shadedWithGroups[group][row][col] = draft.shadedWithGroups[
            group
          ][row][col]
            ? 0
            : 1;
        }),
      },
    });
  };

  const {
    handleMultiSelectStart,
    handleMultiSelectEnd,
    handleMultiSelectPreview,
    handleTouchMultiSelect,
    multiSelectPreview,
  } = useMultiCellSelect(
    response?.shadedWithGroups || buildMatrix(),
    (response) => {
      dispatchPlayer({
        type: "RESPONSE",
        data: {
          id: props.data.id,
          response: {
            shadedWithGroups: response,
          },
        },
      });
    },
  );

  // Score responses
  let mark: "incorrect" | "correct" | undefined;
  if (player.scoreResponses && response) {
    const score = scoreFractionModel(props.data, response);

    mark = score.max === score.actual ? "correct" : "incorrect";
  }

  // if we're missing response, it will be immediately provided by the useEffect above
  if (!response) return null;

  if (groups * rows * columns > MAX_NUMBER_OF_SHADEABLES) {
    return (
      <>
        <div>
          <p>
            This question has too many cells to shade. Please contact your
            teacher.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <Prompt data={props.data.content.prompt} />
      <Toolbar>
        <Button
          title="Clear"
          onClick={() => {
            dispatchPlayer({
              type: "RESPONSE",
              data: {
                id: props.data.id,
                response: {
                  shadedWithGroups: buildMatrix(),
                },
              },
            });
          }}
        >
          Clear
        </Button>
      </Toolbar>
      <StyledFigure>
        {props.data.config.model?.caption && (
          <StyledFigcaption>{props.data.config.model.caption}</StyledFigcaption>
        )}
        <Styled mark={mark}>
          {mark && <Mark type={mark} />}
          {props.data.config.model?.shape === "bar" && (
            <>
              {response.shadedWithGroups?.map((groups, groupIdx) => (
                <Matrix key={`group-${groupIdx}`} data-cy="group">
                  {groups.map((rows, rowIdx) => (
                    <Row key={`row-${groupIdx}-${rowIdx}`} data-cy="row">
                      {rows.map((cols, colIdx) => (
                        <ItemFractionModelCell
                          key={`cell-${groupIdx}-${rowIdx}-${colIdx}`}
                          data-cy="cell"
                          data-group={groupIdx}
                          data-row={rowIdx}
                          data-col={colIdx}
                          selected={
                            multiSelectPreview
                              ? !!multiSelectPreview[groupIdx][rowIdx][colIdx]
                              : !!response.shadedWithGroups?.[groupIdx]?.[
                                  rowIdx
                                ]?.[colIdx]
                          }
                          onClick={() =>
                            handleCellClick(groupIdx, rowIdx, colIdx)
                          }
                          onMouseDown={(e) =>
                            handleMultiSelectStart(e, groupIdx, rowIdx, colIdx)
                          }
                          onMouseUp={(e) =>
                            handleMultiSelectEnd(e, groupIdx, rowIdx, colIdx)
                          }
                          onMouseEnter={(e) =>
                            handleMultiSelectPreview(groupIdx, rowIdx, colIdx)
                          }
                          onTouchStart={(e) =>
                            handleTouchMultiSelect(e, groupIdx, rowIdx, colIdx)
                          }
                        />
                      ))}
                    </Row>
                  ))}
                </Matrix>
              ))}
            </>
          )}
          {props.data.config.model?.shape === "pie" &&
            response.shadedWithGroups && (
              <>
                {response.shadedWithGroups[0]?.map((groups, groupIdx) => (
                  <Matrix
                    key={`group-${groupIdx}`}
                    data-cy="group"
                    style={{ borderColor: "transparent" }}
                  >
                    <PieChart
                      data={groups}
                      onClick={(i) => {
                        handleCellClick(0, groupIdx, i);
                      }}
                    />
                  </Matrix>
                ))}
              </>
            )}
        </Styled>
      </StyledFigure>
    </>
  );
};

export default FractionModel;

type MultiSelectNode = {
  group: number;
  row: number;
  col: number;
};
const useMultiCellSelect = (
  shadedWithGroups: number[][][],
  dispatchPlayer: (response: number[][][]) => void,
) => {
  const [startNode, setStartNode] = React.useState<MultiSelectNode | null>(
    null,
  );
  const [hoveredNode, setHoveredNode] = React.useState<MultiSelectNode | null>(
    null,
  );

  // the cells that are shaded between the start and end cell
  const calculateMultiSelectResult =
    (nodeOne: MultiSelectNode | null, nodeTwo: MultiSelectNode | null) =>
    (draft: number[][][]) => {
      if (!nodeOne) return;
      if (!nodeTwo) return;

      if (nodeOne.group !== nodeTwo.group) return draft;

      // use the value of the start cell to determine if we are adding or removing
      // if you are in ADD mode, set the value to 1
      // if you are in REMOVE mode, set the value to 0
      const value = draft[nodeOne.group][nodeOne.row][nodeOne.col] ? 0 : 1;

      const group = nodeOne.group;

      const startRow = Math.min(nodeTwo.row, nodeOne.row);
      const endRow = Math.max(nodeTwo.row, nodeOne.row);
      const startCol = Math.min(nodeTwo.col, nodeOne.col);
      const endCol = Math.max(nodeTwo.col, nodeOne.col);

      for (let r = startRow; r <= endRow; r++) {
        for (let c = startCol; c <= endCol; c++) {
          draft[group][r][c] = value;
        }
      }

      return draft;
    };

  const handleMultiSelectStart = (
    e: React.MouseEvent | React.TouchEvent,
    group: number,
    row: number,
    col: number,
  ) => {
    e.preventDefault();

    setStartNode({
      group,
      row,
      col,
    });
    setHoveredNode(null);

    // ensure mouse up events are captured even if the mouse leaves the area
    if (e.type === "mousedown") {
      document.addEventListener("mouseup", () => {
        setStartNode(null);
        setHoveredNode(null);
      });
    } else if (e.type === "touchstart") {
      document.addEventListener("touchend", () => {
        setStartNode(null);
        setHoveredNode(null);
      });
      document.addEventListener("touchcancel", () => {
        setStartNode(null);
        setHoveredNode(null);
      });
    }
  };

  const handleMultiSelectPreview = (
    group: number,
    row: number,
    col: number,
  ) => {
    setHoveredNode({
      group,
      row,
      col,
    });
  };

  const handleMultiSelectEnd = (
    e: React.MouseEvent | React.TouchEvent,
    group: number,
    row: number,
    col: number,
  ) => {
    // make sure we dont step on the click event
    if (startNode === null) return;
    if (
      startNode.group === group &&
      startNode.row === row &&
      startNode.col === col
    ) {
      return;
    }

    // we reacalculate this (instead of using the preview) so that
    // we don't have to wait for a re-render to get the correct result
    const multiSelectResult = produce(
      shadedWithGroups,
      calculateMultiSelectResult(startNode, { group, row, col }),
    );

    dispatchPlayer(multiSelectResult);
  };

  const determineNodeFromTouchEvent = (e: TouchEvent) => {
    const touch = e.touches[0] ?? e.changedTouches[0];
    if (!touch) return null;

    const element = document.elementFromPoint(touch.clientX, touch.clientY);

    if (!element) return null;

    if (element instanceof HTMLElement) {
      const group = Number(element.dataset.group);
      const row = Number(element.dataset.row);
      const col = Number(element.dataset.col);
      return { group, row, col };
    }
    return null;
  };

  // touch is different enough that we need a separate handler
  // (touchstart and touchend are not the same as mousedown and mouseup,
  // and there is no equivalent to mouseenter)
  const handleTouchMultiSelect = (
    e: React.TouchEvent,
    group: number,
    row: number,
    col: number,
  ) => {
    handleMultiSelectStart(e, group, row, col);

    const canceler = new AbortController();

    // touchend always fires on the target of the touchstart event
    // (not on where the finger is lifted)
    e.target.addEventListener(
      "touchend",
      (tee) => {
        // instanceof TouchEvent does not work with cypress
        if ("touches" in tee) {
          const endNode = determineNodeFromTouchEvent(tee as TouchEvent);
          if (!endNode) return;

          const node = { group, row, col };

          // make sure we dont step on the click event
          if (
            node.group === endNode.group &&
            node.row === endNode.row &&
            node.col === endNode.col
          ) {
            return;
          }

          // we reacalculate this (instead of using the preview) so that
          // we don't have to wait for a re-render to get the correct result
          const multiSelectResult = produce(
            shadedWithGroups,
            calculateMultiSelectResult(node, endNode),
          );

          dispatchPlayer(multiSelectResult);
        }

        canceler.abort();
      },
      { signal: canceler.signal },
    );

    e.target.addEventListener(
      "touchcancel",
      () => {
        canceler.abort();
      },
      { signal: canceler.signal },
    );

    e.target.addEventListener(
      "touchmove",
      (e) => {
        if ("touches" in e) {
          // prevent scrolling
          e.preventDefault();

          const node = determineNodeFromTouchEvent(e as TouchEvent);
          if (!node) return;

          handleMultiSelectPreview(node.group, node.row, node.col);
        }
      },
      { signal: canceler.signal },
    );
  };

  // calculate a preview using the hovered node
  const multiSelectPreview = produce(
    shadedWithGroups,
    calculateMultiSelectResult(startNode, hoveredNode),
  );

  return {
    handleMultiSelectStart,
    handleMultiSelectEnd,
    handleMultiSelectPreview,
    handleTouchMultiSelect,
    multiSelectPreview,
  };
};
